import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu.js';
import { cn } from '../../lib/utils.js';

export interface SelectOption<V = string> {
  value: V;
  label: string;
}

export interface SelectProps<V> {
  options: SelectOption<V>[];
  value: V | null | undefined;
  onChange: (next: V) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** When the option list is long, virtualizing is overkill — but we cap
   *  the popover height with overflow scroll for usability. */
  maxHeight?: number;
  /** Show a search box at the top of the dropdown. Default: true. */
  searchable?: boolean;
  searchPlaceholder?: string;
}

// Light dropdown-style select. Inputs match `<Input>` height/border to keep
// the form visually consistent. Items show a checkmark on the active one.
export function Select<V extends string>({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled,
  className,
  maxHeight = 320,
  searchable = true,
  searchPlaceholder = 'Search…',
}: SelectProps<V>) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);
  const firstItemRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    if (!searchable) return undefined;
    // Radix's DropdownMenu auto-focuses the first item on open. Wait for
    // that pass to finish, then steal focus back to the search input.
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, searchable]);

  const selected = options.find((o) => o.value === value);
  const filtered = React.useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-11 w-full items-center justify-between gap-2 rounded-md border border-border bg-card px-3.5 py-2.5 text-left text-sm transition-colors',
            'hover:border-[hsl(var(--border-strong))] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background focus-visible:border-transparent',
            'data-[state=open]:border-[hsl(var(--border-strong))]',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'dark:bg-card-raised dark:border-white/10',
            className,
          )}
        >
          <span
            className={cn(
              'truncate',
              !selected && 'text-muted-foreground/70',
            )}
          >
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] p-0"
        style={{ maxHeight, overflowY: 'auto' }}
      >
        {searchable && (
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-[hsl(var(--hairline))] bg-popover px-2.5 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  firstItemRef.current?.focus();
                  return;
                }
                // Stop Radix typeahead from intercepting character keys
                // while the search input has focus. Escape/Tab still bubble
                // so the menu can close / move focus normally.
                if (e.key !== 'Escape' && e.key !== 'Tab') {
                  e.stopPropagation();
                }
              }}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
            />
          </div>
        )}
        <div className="p-1">
          {filtered.length === 0 ? (
            <div className="px-2 py-1.5 text-[13px] text-muted-foreground">
              No matches
            </div>
          ) : (
            filtered.map((opt, i) => {
              const active = opt.value === value;
              return (
                <DropdownMenuItem
                  key={String(opt.value)}
                  ref={i === 0 ? firstItemRef : undefined}
                  onSelect={() => onChange(opt.value)}
                  className={cn(active && 'text-foreground')}
                >
                  <span className="flex-1 truncate">{opt.label}</span>
                  {active && <Check />}
                </DropdownMenuItem>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
